import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import {
  DataTable,
  TABLE_PAGE_SIZE,
  selectColumn,
  createSortableColumn,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  createCustomColumn,
  getFullName,
  useExportMutation,
  getTranslatedOrganizationRoles,
  TableActionsDropdown,
  OrganizationUser,
  EditOrganizationUserRolesModal,
  Button,
  Badge,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import { updateOrganizationUser } from "@/api/services/organizationUser"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { organizationUserQueryKeys } from "@/api/query-keys/organizations"
import {
  ORGANIZATION_USER_STORAGE_KEY,
  useOrganizationUserFilters,
} from "@/hooks/filters/useOrganizationUserFilters"
import { can } from "@/utils/can"
import { useState } from "react"
import ArchiveOrganizationUserModal from "@/components/modals/organization-user/ArchiveOrganizationUserModal"
import UnarchiveOrganizationUserModal from "@/components/modals/organization-user/UnarchiveOrganizationUserModal"
import { z } from "zod"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"

export const Route = createFileRoute("/beheer/contactpersonen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "contacts"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

const DEFAULT_COLUMN_VISIBILITY = {
  status: false,
}

const ALL_COLUMN_IDS = [
  "id",
  "firstName",
  "function",
  "email",
  "phoneNumber",
  "organizationName",
  "roles",
  "studentsCount",
  "status",
]

function RouteComponent() {
  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/contactpersonen/" })
  const navigate = useNavigate({ from: "/beheer/contactpersonen/" })
  const [activeRow, setActiveRow] = useState<OrganizationUser | null>(null)
  const [editOrganizationUserRoleOpen, setEditOrganizationUserRoleOpen] =
    useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)

  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useOrganizationUserFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...organizationUserQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: ORGANIZATION_USER_STORAGE_KEY,
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const getOrganizationUserActions = (user: OrganizationUser) => [
    ...(can("update", "contacts")
      ? [
          {
            label: "Rollen beheren",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setEditOrganizationUserRoleOpen(true)
            },
          },
        ]
      : []),
    ...(archived
      ? [
          {
            label: "Contactpersoon herstellen",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setIsUnarchiveOpen(true)
            },
          },
        ]
      : [
          {
            label: "Contactpersoon archiveren",
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation()
              setActiveRow(user)
              setIsArchiveOpen(true)
            },
          },
        ]),
  ]

  const columns: ColumnDef<OrganizationUser>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "firstName",
      name: "Naam",
      cell: ({ row }: { row: { original: OrganizationUser } }) =>
        getFullName({
          firstName: row.original.firstName,
          namePrefix: row.original.namePrefix,
          lastName: row.original.lastName,
        }),
      accessorKey: "firstName",
    }),
    createSortableColumn({ accessorKey: "function", name: "Functie" }),
    createCustomColumn({
      id: "roles",
      name: "Rollen",
      cell: ({ row }: { row: { original: OrganizationUser } }) =>
        getTranslatedOrganizationRoles(row.original.roles),
      accessorKey: "roles",
      enableSorting: false,
    }),
    createSortableColumn({ accessorKey: "email", name: "E-mailadres" }),
    createSortableColumn({ accessorKey: "phoneNumber", name: "Tel. nr." }),
    createSortableColumn({
      accessorKey: "organizationName",
      name: "Organisatie",
    }),
    createCustomColumn({
      id: "studentsCount",
      name: "Aantal cursisten",
      cell: ({ row }) => row.original.studentsCount,
      accessorKey: "studentsCount",
    }),
    createCustomColumn({
      id: "status",
      name: "Status",
      enableSorting: false,
      cell: ({ row }) => (
        <Badge
          variant={row.original.archivedAt !== null ? "lightGrey" : "green"}
        >
          {row.original.archivedAt ? "Non-actief" : "Actief"}
        </Badge>
      ),
      accessorKey: "status",
    }),

    createCustomColumn({
      id: "actions",
      name: "",
      cell: ({ row }) => {
        return (
          <TableActionsDropdown
            row={row}
            actions={getOrganizationUserActions(row.original)}
          />
        )
      },
      enableSorting: false,
      enableHiding: false,
    }),
  ]

  const { mutate: exportOrganizationUsers } = useExportMutation({
    exportApiPrefix: "organization_users",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  const handleNavigateToDetail = (
    organizationUserId: OrganizationUser["id"],
  ) => {
    router.navigate({
      to: "/beheer/contactpersonen/$organizationUserId/gegevens",
      params: { organizationUserId: organizationUserId.toString() },
      search: { from: router.state.location.href },
    })
  }

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Contactpersonen"
        data={data?.data.users || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        onRowClick={handleNavigateToDetail}
        columns={columns}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportOrganizationUsers,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuw contactpersoon",
                to: "/beheer/contactpersonen/aanmaken",
                entity: "contacts",
                permissions: "create",
              }}
            />
          </>
        }
      />

      {activeRow && editOrganizationUserRoleOpen && (
        <EditOrganizationUserRolesModal
          isOpen={editOrganizationUserRoleOpen}
          setIsOpen={setEditOrganizationUserRoleOpen}
          activeRow={activeRow}
          listQueryKey={organizationUserQueryKeys.all}
          userQueryKey={organizationUserQueryKeys.user(activeRow.id)}
          updateOrganizationUserFn={updateOrganizationUser}
          invalidateFilters={(queryClient) => {
            queryClient.invalidateQueries({
              queryKey: organizationUserQueryKeys.all,
            })
          }}
        />
      )}

      {activeRow && isArchiveOpen && (
        <ArchiveOrganizationUserModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          organizationUser={activeRow}
        />
      )}

      {activeRow && isUnarchiveOpen && (
        <UnarchiveOrganizationUserModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          organizationUser={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
